import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { apiClient } from '@/lib/axios';
import type { Mcp, McpGroup, McpFilters, PaginatedResponse } from '@/types/api';

// ── Helpers ────────────────────────────────────────────────────────────────
const normalizeId = (id: string): string => id.replace(/^(mcp)_/, '');

export interface McpGroupFilters {
  page?: number;
  pageSize?: number;
  search?: string;
}

export const mcpKeys = {
  all: ['mcps'] as const,
  grouped: (filters?: McpGroupFilters) => [...mcpKeys.all, 'grouped', filters ?? {}] as const,
  list: (filters: McpFilters) => [...mcpKeys.all, 'list', filters] as const,
  detail: (id: string) => [...mcpKeys.all, 'detail', id] as const,
};

export function useMcpsGrouped(
  filters: McpGroupFilters = {},
  staleTime = 30_000,
  options: { enabled?: boolean; refetchInterval?: number } = {},
) {
  return useQuery({
    queryKey: mcpKeys.grouped(filters),
    queryFn: async () => {
      const { data } = await apiClient.get<PaginatedResponse<McpGroup>>('/mcps/grouped', { params: filters });
      return data;
    },
    staleTime,
    enabled: options.enabled ?? true,
    refetchInterval: options.refetchInterval,
  });
}

export function useMcps(filters: McpFilters = {}) {
  return useQuery({
    queryKey: mcpKeys.list(filters),
    // Keep the current rows on screen while a new sort / page / filter loads.
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const { data } = await apiClient.get<PaginatedResponse<Mcp>>('/mcps', { params: filters });
      return data;
    },
    staleTime: 30_000,
  });
}

export interface McpStats {
  total: number;
  active: number;
  error: number;
  totalTools: number;
}

/** Aggregate KPI numbers matching the same filters as useMcps — for the MCPs page's KPI strip, without fetching hundreds of full MCP rows. */
export function useMcpsStats(filters: McpFilters = {}) {
  return useQuery({
    queryKey: [...mcpKeys.all, 'stats', filters] as const,
    queryFn: async () => {
      const { data } = await apiClient.get<McpStats>('/mcps/stats', { params: filters });
      return data;
    },
    staleTime: 30_000,
  });
}

export function useMcp(id: string) {
  const normalizedId = normalizeId(id);
  return useQuery({
    queryKey: mcpKeys.detail(normalizedId),
    queryFn: async () => {
      const { data } = await apiClient.get<Mcp>(`/mcps/${normalizedId}`);
      return data;
    },
    enabled: Boolean(normalizedId),
  });
}

export function useCreateMcp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: { name: string; vendor: string; mcpUrl: string }) => {
      const { data } = await apiClient.post<Mcp>('/mcps', dto);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: mcpKeys.all }),
  });
}

export function useUpdateMcp(id: string) {
  const normalizedId = normalizeId(id);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: Partial<{ name: string; vendor: string; mcpUrl: string; status: Mcp['status']; toolCount: number }>) => {
      const { data } = await apiClient.patch<Mcp>(`/mcps/${normalizedId}`, dto);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: mcpKeys.all });
      qc.invalidateQueries({ queryKey: mcpKeys.detail(normalizedId) });
    },
  });
}

export function useDeleteMcp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const normalizedId = normalizeId(id);
      await apiClient.delete(`/mcps/${normalizedId}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: mcpKeys.all }),
  });
}
